import { TileSize } from "game-common/models"
import { Circle, Container, Graphics, Text } from "pixi.js"
import { BuildingPrintGroup } from "../../../../client_models"
import { SCALER } from "../../../../client_util"
import { TextRenderer } from "../text/text_renderer"
import { easeOutCubic, interpolateColor } from "./dial"

// The dial over a batch of walls that have been placed but not yet printed.
//
// It is TWO things at once, and the drawing has to say both: a clock running down the few seconds
// before the batch commits, and the button that calls the batch off. So the ring drains (the window
// is closing, not the wall rising) and the middle of the dial is the cancel target — showing the
// seconds left at rest, and swapping to a cross the moment the pointer is on it, which is the only
// moment "click to cancel" is the useful thing to say.
//
// These dials are children of the world surface. Their tile coordinates never depend on the player,
// the mouse or the camera; they travel through the same transforms as the walls they belong to.

// Base radius. The world surface is at surfaceZoom, which `root.scale` undoes, so everything here is
// in SCREEN pixels against a 64px-on-screen tile — the old 13px dial with a 6.5px readout under it
// was about a tenth of a tile tall and could not be read at all while building.
const RADIUS = 17
const RING_RADIUS = RADIUS - 3.5
const RING_WIDTH = 2.5

const FACE_COLOR = 0x20262d
const FACE_HOVER_COLOR = 0x45211d
const BORDER_COLOR = 0x8e7b58
const BORDER_HOVER_COLOR = 0xff8f74
const GROOVE_COLOR = 0x454b54
const RING_COLOR = 0xffd166
const RING_LATE_COLOR = 0xff9d5c
const RING_HOVER_COLOR = 0xff7a5c
const INK_COLOR = 0xffedc7
const INK_HOVER_COLOR = 0xffd9cf

// Pop-in, and how much bigger the dial gets under the pointer.
const APPEAR_MS = 170
const HOVER_SCALE = 1.14

interface CountdownRow {
    root: Container
    wheel: Graphics
    time: Text
    count: Text
    group: BuildingPrintGroup
    appearedAt: number
    // What was last painted, so a dial whose arc has not visibly moved costs nothing.
    drawn: string
}

export class BuildingPrintCountdown extends Container {
    private rows = new Map<number, CountdownRow>()
    private hovered = new Set<number>()

    constructor(private worldSurface: () => Container) {
        super()
        this.zIndex = 100001 // Above pending wall ghosts.
        this.visible = false
    }

    isPointerOver = () => this.visible && this.hovered.size > 0

    setGroups = (groups: BuildingPrintGroup[]) => {
        const ids = new Set(groups.map(group => group.id))
        this.rows.forEach((row, id) => {
            if (ids.has(id)) return
            this.removeChild(row.root)
            row.root.destroy({ children: true })
            this.rows.delete(id)
            this.hovered.delete(id)
        })
        groups.forEach(group => {
            let row = this.rows.get(group.id)
            if (!row) {
                const root = new Container()
                root.interactive = true
                root.cursor = "pointer"
                root.hitArea = new Circle(0, 0, RADIUS + 3)
                root.on("pointerover", () => this.hovered.add(group.id))
                root.on("pointerout", () => this.hovered.delete(group.id))
                root.on("pointerdown", event => {
                    event.stopPropagation()
                    this.rows.get(group.id)?.group.onCancel()
                })
                const wheel = new Graphics()
                root.addChild(wheel)
                // The seconds live in the MIDDLE of the dial now, where the cancel cross used to be:
                // one glance tells you how long you have, and the cross takes over on hover.
                // 12px: the clear disc inside the groove is only ~24px across, and the numeral has
                // to sit wholly inside it without crowding the ring.
                const time = new TextRenderer().fontSize(12).color(INK_COLOR).bold()
                    .align("center").strokeThickness(2).render("") as Text
                time.anchor.set(0.5)
                root.addChild(time)
                // How many cells are about to print. Only ever drawn for a real batch — a single
                // wall does not need telling that it is one wall.
                // This one sits OUTSIDE the dial, over whatever the world has there, so it keeps a
                // full outline — 10px carries one without the stroke closing up the glyphs.
                const count = new TextRenderer().fontSize(10).color(INK_COLOR).bold()
                    .align("center").strokeThickness(2).render("") as Text
                count.anchor.set(0.5)
                count.y = RADIUS + 6
                root.addChild(count)
                row = { root, wheel, time, count, group, appearedAt: Date.now(), drawn: "" }
                this.rows.set(group.id, row)
                this.addChild(root)
            }
            row.group = group
            // Set once from the group's final tile, in world pixels.
            row.root.position.set((group.anchor.x + 0.5) * TileSize, (group.anchor.y + 0.5) * TileSize)
        })
        this.visible = groups.length > 0
        this.update()
    }

    update = () => {
        if (!this.visible) return
        const surface = this.worldSurface()
        if (this.parent !== surface) surface.addChild(this)
        const now = Date.now()
        this.rows.forEach(row => this.updateRow(row, now))
    }

    private updateRow = (row: CountdownRow, now: number) => {
        const { group } = row
        const remaining = Math.max(0, group.duration - (now - group.startedAt))
        const fraction = group.duration > 0 ? Math.min(1, remaining / group.duration) : 0
        const hover = this.hovered.has(group.id)
        const appear = Math.min(1, (now - row.appearedAt) / APPEAR_MS)

        // Quantize what the paint actually depends on. A dial whose arc has not moved a visible
        // step, and which nothing is hovering, must not repaint: a wall batch can be several of
        // these and the frame budget here is shared with the whole map.
        // 120 steps around a ~85px circumference is sub-pixel, and halves the repaints at 60fps.
        const key = `${Math.round(fraction * 120)}:${hover ? 1 : 0}:${Math.round(appear * 20)}:${group.count}`
        if (key === row.drawn) return
        row.drawn = key

        // Correct the stage's vertical stretch while preserving the tile anchor, and pop in (then
        // swell under the pointer) by scaling the whole dial about that same anchor.
        const grow = (0.72 + 0.28 * easeOutCubic(appear)) * (hover ? HOVER_SCALE : 1)
        row.root.scale.set(0.5 * grow, (0.5 / SCALER) * grow)
        row.root.alpha = appear

        const seconds = String(Math.max(1, Math.ceil(remaining / 1000)))
        if (row.time.text !== seconds) row.time.text = seconds
        row.time.visible = !hover
        row.time.tint = hover ? INK_HOVER_COLOR : INK_COLOR

        const batch = group.count > 1 ? `x${group.count}` : ""
        if (row.count.text !== batch) row.count.text = batch
        row.count.visible = batch.length > 0

        this.drawWheel(row.wheel, fraction, hover)
    }

    private drawWheel = (wheel: Graphics, fraction: number, hover: boolean) => {
        wheel.clear()

        // Dropped silhouette: the dial sits over wall ghosts and grass, and needs to read as an
        // object on top of them rather than a decal painted into them.
        wheel.beginFill(0x000000, 0.26).drawCircle(0, 2, RADIUS).endFill()

        wheel.lineStyle(1, hover ? BORDER_HOVER_COLOR : BORDER_COLOR, hover ? 1 : 0.9)
        wheel.beginFill(hover ? FACE_HOVER_COLOR : FACE_COLOR, 0.97).drawCircle(0, 0, RADIUS - 1).endFill()

        // The empty groove the arc drains along.
        wheel.lineStyle(RING_WIDTH, GROOVE_COLOR, 0.9).drawCircle(0, 0, RING_RADIUS)

        if (fraction > 0) {
            // Warms from gold toward orange as the window closes, and goes danger-red under the
            // pointer, where the dial is a cancel button rather than a clock.
            const color = hover ? RING_HOVER_COLOR : interpolateColor(RING_LATE_COLOR, RING_COLOR, fraction)
            const angle = -Math.PI / 2 + fraction * Math.PI * 2
            // Fat faint copy first: a glow without a filter, which this hardware target cannot spare.
            wheel.lineStyle(RING_WIDTH + 2.5, color, 0.26)
                .moveTo(0, -RING_RADIUS).arc(0, 0, RING_RADIUS, -Math.PI / 2, angle)
            wheel.lineStyle(RING_WIDTH, color)
                .moveTo(0, -RING_RADIUS).arc(0, 0, RING_RADIUS, -Math.PI / 2, angle)
            // The moving endpoint makes the shrinking countdown arc easy to read.
            wheel.lineStyle(0).beginFill(INK_COLOR)
                .drawCircle(Math.cos(angle) * RING_RADIUS, Math.sin(angle) * RING_RADIUS, 2).endFill()
        }

        // A clock's twelve-o'clock notch reinforces that the shrinking ring is a timer.
        wheel.lineStyle(1, INK_COLOR, 0.8).moveTo(0, -RADIUS + 1).lineTo(0, -RING_RADIUS - RING_WIDTH / 2)

        // The cross is the hover state: at rest the middle of the dial is the countdown, and the
        // affordance appears exactly when it can be acted on.
        if (hover) {
            const arm = 4.5
            wheel.lineStyle(2, INK_HOVER_COLOR)
                .moveTo(-arm, -arm).lineTo(arm, arm).moveTo(arm, -arm).lineTo(-arm, arm)
        }
    }
}
